import {Permissions} from '@amzn/f3-excelsior-portal-permissions-api-client';

/**
 * sessionStorage cache for the Permissions API POST /aws-credentials responses (STS credentials).
 * Lets repeat page loads within a browser tab session skip the STS round-trips.
 * Only sessionStorage is used so credentials never outlive the tab session.
 */
export const CACHE_KEY_PREFIX = 'excelsior_aws_creds_';
export const CACHE_KEY_GLOBAL = 'excelsior_aws_creds_global';

// Treat credentials as expired 5 minutes before actual expiry to avoid using nearly-expired creds
export const EXPIRY_BUFFER_MS = 5 * 60 * 1000;

// Max age for entries without credentials: STS default 1h TTL minus the buffer
export const NO_CREDENTIALS_MAX_AGE_MS = 60 * 60 * 1000 - EXPIRY_BUFFER_MS;

interface CachedCredentials {
    response: Permissions.Types.GetAwsCredentialsResponse;
    // The businessContext the credentials were requested for; a different context is a cache miss
    businessContext: string[];
    cachedAt: number;
}

function getCacheKey(regionId?: string): string {
    return regionId ? `${CACHE_KEY_PREFIX}${regionId}` : CACHE_KEY_GLOBAL;
}

/**
 * Expiration is typed as a string. The API returns Unix epoch milliseconds (see generateRefreshableCredentials),
 * but fall back to Date.parse for ISO date strings. Returns NaN when unparseable.
 */
function parseExpiration(expiration: unknown): number {
    if (typeof expiration === 'number') {
        return expiration;
    }
    if (typeof expiration !== 'string' || expiration.trim() === '') {
        return NaN;
    }
    const numeric = Number(expiration);
    return Number.isNaN(numeric) ? Date.parse(expiration) : numeric;
}

function isSameBusinessContext(a: unknown, b: string[]): boolean {
    return Array.isArray(a) && a.length === b.length && a.every((v, i) => v === b[i]);
}

export function getCachedCredentials(
    businessContext: string[],
    regionId?: string
): Permissions.Types.GetAwsCredentialsResponse | null {
    const key = getCacheKey(regionId);
    try {
        const raw = sessionStorage.getItem(key);
        if (!raw) {
            return null;
        }
        const cached: CachedCredentials = JSON.parse(raw);
        const credentials = cached?.response?.credentials;

        const now = Date.now();
        let allValid: boolean;
        if (Array.isArray(credentials) && credentials.length === 0) {
            // e.g. the global call (accessList/globalScopes only): no expiration to check, so use the entry's age
            allValid = typeof cached.cachedAt === 'number' && now - cached.cachedAt < NO_CREDENTIALS_MAX_AGE_MS;
        } else {
            // Every credential must be valid beyond the buffer; missing credentials are invalid
            allValid =
                Array.isArray(credentials) &&
                credentials.every((cred) => {
                    const expiry = parseExpiration(cred?.awsCredentials?.expiration);
                    return !Number.isNaN(expiry) && expiry - EXPIRY_BUFFER_MS > now;
                });
        }

        if (!allValid) {
            sessionStorage.removeItem(key);
            return null;
        }
        if (!isSameBusinessContext(cached.businessContext, businessContext)) {
            // Scoped to a different business context; the next fetch overwrites this entry
            return null;
        }
        return cached.response;
    } catch {
        return null;
    }
}

export function setCachedCredentials(
    response: Permissions.Types.GetAwsCredentialsResponse,
    businessContext: string[],
    regionId?: string
): void {
    try {
        const cached: CachedCredentials = {
            response,
            businessContext,
            cachedAt: Date.now(),
        };
        sessionStorage.setItem(getCacheKey(regionId), JSON.stringify(cached));
    } catch {
        // sessionStorage full or unavailable; degrade silently
    }
}

export function clearCachedCredentials(): void {
    try {
        // CACHE_KEY_GLOBAL also starts with CACHE_KEY_PREFIX
        Object.keys(sessionStorage)
            .filter((k) => k.startsWith(CACHE_KEY_PREFIX))
            .forEach((k) => sessionStorage.removeItem(k));
    } catch {
        // ignore
    }
}
